import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  Boxes,
  FileCheck2,
  Workflow,
  Plus,
  X,
  Trash2,
  ShieldCheck,
  Sparkles,
  Loader2,
  UploadCloud,
  Info,
} from "lucide-react";
import axios from "axios";
import { Section } from "@/components/Section";
import { clientUid } from "@/lib/clientUid";
import { devWarn } from "@/lib/devlog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VENDOR_CATEGORIES = [
  { id: "diagnostics_labs", label: "Diagnostics / Labs" },
  { id: "wellness_services", label: "Wellness Services" },
  { id: "remote_monitoring", label: "Remote Monitoring" },
  { id: "digital_health", label: "Digital Health" },
  { id: "fulfillment_logistics", label: "Fulfillment / Logistics" },
  { id: "pharmacy", label: "Pharmacy" },
  { id: "other", label: "Other" },
];

const STEPS = [
  { id: "company", label: "Company", icon: Building2 },
  { id: "services", label: "Products & Services", icon: Boxes },
  { id: "documents", label: "Documents", icon: FileCheck2 },
  { id: "operational", label: "Operations", icon: Workflow },
  { id: "review", label: "Review & Submit", icon: Sparkles },
];

const DOC_FIELDS = [
  { id: "w9", label: "W-9", required: true, hint: "PDF or image, ≤5MB" },
  { id: "coi", label: "Certificate of Insurance", required: true, hint: "PDF preferred, ≤5MB" },
  { id: "license", label: "Licenses / Accreditations", required: false, hint: "Optional · PDF or image, ≤5MB" },
];

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-700 mb-1.5";

function emptyService() {
  return {
    _uid: clientUid("va-svc"),
    name: "",
    description: "",
    products: [{ _uid: clientUid("va-prod"), name: "", description: "" }],
    clinic_use_cases: "",
    fulfillment_timeline: "",
    recurring_capable: false,
    client_bill_compatible: false,
    sla: "",
  };
}

function blankForm() {
  return {
    company: {
      legal_name: "",
      website: "",
      headquarters_location: "",
      primary_contact: "",
      support_email: "",
      vendor_category: "",
    },
    services: [emptyService()],
    documents: { w9: null, coi: null, license: null },
    operational: {
      onboarding_process: "",
      support_process: "",
      invoicing_process: "",
      recurring_fulfillment_capable: false,
      states_serviced: "",
    },
  };
}

function isValidEmail(v) {
  return /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(v || "");
}

export default function VendorApply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editToken = params.get("edit_token");
  const startQuery = params.get("start") === "1";

  const [form, setForm] = useState(blankForm());
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(!!editToken);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [clarification, setClarification] = useState(null);
  const [started, setStarted] = useState(!!editToken || startQuery);

  useEffect(() => {
    let cancelled = false;
    if (!editToken) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/vendor-application/by-token/${editToken}`);
        if (cancelled) return;
        const d = r.data || {};
        setForm({
          company: d.company || blankForm().company,
          services:
            (d.services && d.services.length ? d.services : [emptyService()]).map((s) => ({
              ...emptyService(),
              ...s,
              // Inject a fresh `_uid` from emptyService() above, but preserve
              // it when the loaded service already carries one (idempotent).
              _uid: s._uid || clientUid("va-svc"),
              products:
                (s.products && s.products.length ? s.products : [{ name: "", description: "" }]).map((p) => ({
                  _uid: p._uid || clientUid("va-prod"),
                  ...p,
                })),
            })),
          documents: { w9: null, coi: null, license: null },
          operational: d.operational || blankForm().operational,
        });
        setClarification(d.clarification_request || null);
      } catch (e) {
        devWarn("[VendorApply] could not load application:", e);
        toast.error("Could not load application — link may have expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editToken]);

  const completion = useMemo(() => computeCompletion(form), [form]);
  const missing = useMemo(() => computeMissing(form), [form]);
  const stepValid = useMemo(() => validateStep(STEPS[step].id, form), [form, step]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = JSON.parse(JSON.stringify(form));
      Object.keys(payload.documents).forEach((k) => {
        if (!payload.documents[k]) delete payload.documents[k];
      });
      // Strip client-only `_uid` from services/products before sending, and
      // drop any completely empty service rows (public short form leaves
      // services blank on purpose — we don't want to persist noise).
      payload.services = (payload.services || [])
        .filter((s) => (s.name || "").trim() || (s.description || "").trim())
        .map(({ _uid, products, ...rest }) => ({ // eslint-disable-line no-unused-vars
          ...rest,
          products: (products || []).map(({ _uid: __, ...p }) => p), // eslint-disable-line no-unused-vars
        }));
      let r;
      if (editToken) {
        r = await axios.patch(
          `${BACKEND_URL}/api/vendor-application/by-token/${editToken}`,
          { ...payload, resubmit: true },
        );
        toast.success("Application resubmitted — admin will review shortly.");
      } else {
        r = await axios.post(`${BACKEND_URL}/api/vendor-application`, payload);
        toast.success("Application submitted.");
      }
      setSubmitted({ id: r.data?.id, edit_token: r.data?.edit_token });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed — please review and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Section className="pt-32" testId="vendor-apply-loading">
        <div className="flex items-center justify-center text-sm text-slate-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading your application…
        </div>
      </Section>
    );
  }

  if (submitted) {
    return <SuccessView submitted={submitted} editing={!!editToken} navigate={navigate} />;
  }

  if (!started) {
    return <LandingView onStart={() => setStarted(true)} />;
  }

  // ------------------------------------------------------------------
  // Public NEW application (no editToken) — beta stage: collect ONLY
  // Company Info. Services / documents / operational answers will be
  // requested later by admin via the existing clarification-token flow.
  // ------------------------------------------------------------------
  if (!editToken) {
    return (
      <PublicShortApplyForm
        form={form}
        setForm={setForm}
        onSubmit={submit}
        submitting={submitting}
      />
    );
  }

  // ------------------------------------------------------------------
  // Admin-initiated FULL profile flow (edit token present) — keep the
  // robust multi-step wizard so vendors can complete services, docs,
  // and operational answers when we ask for them.
  // ------------------------------------------------------------------
  return (
    <div data-testid="vendor-apply-page" className="bg-slate-50 min-h-screen">
      <Hero clarification={clarification} />
      <Section className="pt-2 pb-16">
        <div className="max-w-5xl mx-auto">
          <Stepper step={step} onJump={setStep} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
              <StepForm step={STEPS[step].id} form={form} setForm={setForm} />
              <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between gap-3">
                <button
                  type="button"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  data-testid="vendor-apply-back-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    disabled={!stepValid.ok}
                    data-testid="vendor-apply-next-btn"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {stepValid.ok ? "Continue" : (stepValid.reason || "Complete required fields")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={submitting || missing.length > 0}
                    onClick={submit}
                    data-testid="vendor-apply-submit-btn"
                    className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-3 text-sm font-bold transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                      </>
                    ) : editToken ? (
                      <>Resubmit application <ArrowRight className="w-4 h-4" /></>
                    ) : (
                      <>Submit application <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                )}
              </div>
            </div>
            <Sidebar completion={completion} missing={missing} />
          </div>
        </div>
      </Section>
    </div>
  );
}

function Hero({ clarification }) {
  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-10">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 mb-3">
          Vendor Application
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.04]">
          Apply to Join the Nodera Vendor Network
        </h1>
        <p className="mt-4 text-base text-slate-600 max-w-2xl leading-relaxed">
          Connect your services to clinics deploying modern cash-pay healthcare programs through
          scalable operational infrastructure.
        </p>
        {clarification ? (
          <div
            data-testid="vendor-apply-clarification-banner"
            className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-amber-900">
                  Clarification requested by Nodera operations
                </div>
                {clarification.note ? (
                  <p className="mt-1.5 text-sm text-amber-800 leading-relaxed">{clarification.note}</p>
                ) : null}
                {clarification.reasons && clarification.reasons.length > 0 ? (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {clarification.reasons.map((r) => (
                      <li
                        key={r}
                        className="text-[11px] uppercase tracking-[0.14em] font-bold bg-white border border-amber-200 text-amber-800 rounded-full px-2.5 py-1"
                      >
                        {r.replaceAll("_", " ")}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="mt-3 text-xs text-amber-700">
                  Update your application below and click "Resubmit application" when ready.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Stepper({ step, onJump }) {
  return (
    <ol
      className="grid grid-cols-2 sm:grid-cols-5 gap-2 rounded-2xl border border-slate-200 bg-white p-2"
      data-testid="vendor-apply-stepper"
    >
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const active = i === step;
        const done = i < step;
        return (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onJump(i)}
              data-testid={`vendor-apply-step-${s.id}`}
              className={`w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                active ? "bg-slate-900 text-white" : done ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <span className={`w-6 h-6 rounded-md flex items-center justify-center ${active ? "bg-white/15" : done ? "bg-blue-100" : "bg-slate-100"}`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              </span>
              <span className="truncate">{s.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function Sidebar({ completion, missing }) {
  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-6 h-fit lg:sticky lg:top-24" data-testid="vendor-apply-sidebar">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
        Application progress
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-3xl font-extrabold text-slate-900 tabular-nums" data-testid="vendor-apply-completion-pct">
          {completion}%
        </span>
        <span className="text-xs text-slate-500">complete</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-blue-600 transition-all" style={{ width: `${completion}%` }} />
      </div>
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Checklist</div>
        <ul className="space-y-2 text-sm">
          {missing.length === 0 ? (
            <li className="flex items-center gap-2 text-emerald-700 font-semibold">
              <CheckCircle2 className="w-4 h-4" /> All required items provided
            </li>
          ) : (
            missing.map((m) => (
              <li key={m} className="flex items-center gap-2 text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {m}
              </li>
            ))
          )}
        </ul>
      </div>
      <div className="mt-6 pt-6 border-t border-slate-200">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5" />
          <p className="text-[11px] leading-relaxed text-slate-500">
            Vendors remain independently responsible for compliance. Nodera approval is operational
            network approval only — Nodera does not provide legal or regulatory certification.
          </p>
        </div>
      </div>
    </aside>
  );
}

function StepForm({ step, form, setForm }) {
  if (step === "company") return <CompanyStep form={form} setForm={setForm} />;
  if (step === "services") return <ServicesStep form={form} setForm={setForm} />;
  if (step === "documents") return <DocumentsStep form={form} setForm={setForm} />;
  if (step === "operational") return <OperationalStep form={form} setForm={setForm} />;
  return <ReviewStep form={form} />;
}

function CompanyStep({ form, setForm }) {
  const c = form.company;
  const set = (k) => (v) => setForm({ ...form, company: { ...c, [k]: v } });
  return (
    <div data-testid="vendor-apply-step-company-form">
      <StepHeading title="Company information" sub="Tell us about your organization. Used to route reviews and contracts." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Legal business name" required>
          <input value={c.legal_name} onChange={(e) => set("legal_name")(e.target.value)} data-testid="vendor-apply-legal-name-input" className={inputCls} placeholder="Acme Diagnostics LLC" />
        </Field>
        <Field label="Website">
          <input value={c.website} onChange={(e) => set("website")(e.target.value)} data-testid="vendor-apply-website-input" className={inputCls} placeholder="https://" />
        </Field>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Headquarters location" required>
          <input value={c.headquarters_location} onChange={(e) => set("headquarters_location")(e.target.value)} data-testid="vendor-apply-hq-input" className={inputCls} placeholder="Austin, TX" />
        </Field>
        <Field label="Primary contact" required>
          <input value={c.primary_contact} onChange={(e) => set("primary_contact")(e.target.value)} data-testid="vendor-apply-contact-input" className={inputCls} placeholder="Jane Doe, Director of Partnerships" />
        </Field>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Support email" required>
          <input type="email" value={c.support_email} onChange={(e) => set("support_email")(e.target.value)} data-testid="vendor-apply-support-email-input" className={inputCls} placeholder="support@yourcompany.com" />
        </Field>
        <Field label="Vendor category" required>
          <select value={c.vendor_category} onChange={(e) => set("vendor_category")(e.target.value)} data-testid="vendor-apply-category-select" className={inputCls}>
            <option value="">Select a category</option>
            {VENDOR_CATEGORIES.map((cat) => (<option key={cat.id} value={cat.id}>{cat.label}</option>))}
          </select>
        </Field>
      </div>
    </div>
  );
}

function ServicesStep({ form, setForm }) {
  const setServices = (services) => setForm({ ...form, services });
  const addService = () => setServices([...form.services, emptyService()]);
  const removeService = (idx) => {
    if (form.services.length === 1) return setServices([emptyService()]);
    setServices(form.services.filter((_, i) => i !== idx));
  };
  const update = (idx, patch) => setServices(form.services.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  return (
    <div data-testid="vendor-apply-step-services-form">
      <StepHeading
        title="Products & services"
        sub="Add each service you'd like to make available to clinics. Add one or more products beneath each service."
        helper="Please keep descriptions operational and avoid unnecessary clinical claims."
      />
      <div className="space-y-5">
        {form.services.map((s, idx) => (
          <ServiceCard
            key={s._uid || `svc-idx-${idx}`}
            idx={idx}
            service={s}
            update={(patch) => update(idx, patch)}
            remove={() => removeService(idx)}
            canRemove={form.services.length > 1}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={addService}
        data-testid="vendor-apply-add-service-btn"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
      >
        <Plus className="w-4 h-4" /> Add another service
      </button>
    </div>
  );
}

function ServiceCard({ idx, service, update, remove, canRemove }) {
  const setProducts = (products) => update({ products });
  const addProduct = () => setProducts([...service.products, { _uid: clientUid("va-prod"), name: "", description: "" }]);
  const removeProduct = (pIdx) => {
    if (service.products.length === 1) return setProducts([{ name: "", description: "" }]);
    setProducts(service.products.filter((_, i) => i !== pIdx));
  };
  const updateProduct = (pIdx, patch) =>
    setProducts(service.products.map((p, i) => (i === pIdx ? { ...p, ...patch } : p)));

  return (
    <div data-testid={`vendor-apply-service-card-${idx}`} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">Service #{idx + 1}</div>
        {canRemove ? (
          <button type="button" onClick={remove} data-testid={`vendor-apply-remove-service-${idx}`} className="text-slate-400 hover:text-rose-600" aria-label="Remove service">
            <Trash2 className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-4">
        <Field label="Service name" required>
          <input value={service.name} onChange={(e) => update({ name: e.target.value })} data-testid={`vendor-apply-service-name-${idx}`} className={inputCls} placeholder="Comprehensive Hormone Panel" />
        </Field>
        <Field label="Fulfillment timeline">
          <input value={service.fulfillment_timeline} onChange={(e) => update({ fulfillment_timeline: e.target.value })} data-testid={`vendor-apply-service-timeline-${idx}`} className={inputCls} placeholder="5 business days" />
        </Field>
      </div>
      <Field label="Short description" className="mt-4" required>
        <textarea value={service.description} onChange={(e) => update({ description: e.target.value })} data-testid={`vendor-apply-service-description-${idx}`} rows={3} className={`${inputCls} resize-y`} placeholder="Operational summary of what this service delivers." />
      </Field>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-700">Products in this service</span>
          <button type="button" onClick={addProduct} data-testid={`vendor-apply-add-product-${idx}`} className="text-xs font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add product
          </button>
        </div>
        <div className="space-y-2">
          {service.products.map((p, pIdx) => (
            <div key={p._uid || `prod-${idx}-${pIdx}`} data-testid={`vendor-apply-product-${idx}-${pIdx}`} className="rounded-xl border border-slate-200 bg-white p-3 grid sm:grid-cols-12 gap-2">
              <input value={p.name} onChange={(e) => updateProduct(pIdx, { name: e.target.value })} data-testid={`vendor-apply-product-name-${idx}-${pIdx}`} className={`${inputCls} sm:col-span-4`} placeholder="Product name" />
              <input value={p.description || ""} onChange={(e) => updateProduct(pIdx, { description: e.target.value })} data-testid={`vendor-apply-product-desc-${idx}-${pIdx}`} className={`${inputCls} sm:col-span-7`} placeholder="Short description (optional)" />
              <button type="button" onClick={() => removeProduct(pIdx)} className="sm:col-span-1 text-slate-400 hover:text-rose-600 flex items-center justify-center" aria-label="Remove product">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="Clinic use cases">
          <textarea value={service.clinic_use_cases} onChange={(e) => update({ clinic_use_cases: e.target.value })} data-testid={`vendor-apply-service-usecases-${idx}`} rows={2} className={`${inputCls} resize-y`} placeholder="Programs or scenarios where clinics typically use this service." />
        </Field>
        <Field label="SLA inputs">
          <textarea value={service.sla} onChange={(e) => update({ sla: e.target.value })} data-testid={`vendor-apply-service-sla-${idx}`} rows={2} className={`${inputCls} resize-y`} placeholder="TAT, response window, escalation channel." />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Toggle label="Recurring capable" checked={service.recurring_capable} onChange={(v) => update({ recurring_capable: v })} testid={`vendor-apply-service-recurring-${idx}`} />
        <Toggle label="Supports wholesale / contracted-rate billing to clinic" checked={service.client_bill_compatible} onChange={(v) => update({ client_bill_compatible: v })} testid={`vendor-apply-service-clientbill-${idx}`} />
      </div>
    </div>
  );
}

function DocumentsStep({ form, setForm }) {
  const setDocs = (documents) => setForm({ ...form, documents });
  const handleFile = (kind) => async (file) => {
    if (!file) {
      setDocs({ ...form.documents, [kind]: null });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name} is larger than 5 MB.`);
      return;
    }
    const buf = await file.arrayBuffer();
    let binary = "";
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    const b64 = window.btoa(binary);
    setDocs({ ...form.documents, [kind]: { filename: file.name, mime: file.type, size_bytes: file.size, base64: b64 } });
  };
  return (
    <div data-testid="vendor-apply-step-documents-form">
      <StepHeading title="Required documents" sub="Upload supporting documents. We do not OCR or parse files — they're stored for admin review only." />
      <div className="space-y-3">
        {DOC_FIELDS.map((d) => (<DocRow key={d.id} field={d} value={form.documents[d.id]} onChange={handleFile(d.id)} />))}
      </div>
      <p className="mt-5 text-[11px] leading-relaxed text-slate-500">
        Vendors maintain independent compliance and licensing. Nodera does not certify documents — admin
        review confirms only that the document is on file.
      </p>
    </div>
  );
}

function DocRow({ field, value, onChange }) {
  const uploaded = !!value;
  return (
    <div data-testid={`vendor-apply-doc-row-${field.id}`} className={`rounded-2xl border ${uploaded ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"} p-4 flex items-start gap-4`}>
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${uploaded ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
        {uploaded ? <CheckCircle2 className="w-5 h-5" /> : <UploadCloud className="w-5 h-5" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-bold text-slate-900">{field.label}</span>
          {field.required ? (
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-rose-600">Required</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">Optional</span>
          )}
        </div>
        {uploaded ? (
          <div className="mt-1 text-xs text-emerald-800 truncate" data-testid={`vendor-apply-doc-uploaded-${field.id}`}>
            {value.filename} · {(value.size_bytes / 1024).toFixed(0)} KB
          </div>
        ) : (
          <div className="mt-1 text-xs text-slate-500">{field.hint}</div>
        )}
      </div>
      <label className="cursor-pointer text-xs font-semibold rounded-full px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700" data-testid={`vendor-apply-doc-upload-${field.id}`}>
        {uploaded ? "Replace" : "Upload"}
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,.heif" className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
      {uploaded ? (
        <button type="button" onClick={() => onChange(null)} className="text-xs text-slate-400 hover:text-rose-600 px-2" aria-label="Remove file">
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  );
}

function OperationalStep({ form, setForm }) {
  const o = form.operational;
  const set = (k) => (v) => setForm({ ...form, operational: { ...o, [k]: v } });
  return (
    <div data-testid="vendor-apply-step-operational-form">
      <StepHeading title="Operational questions" sub="A few short answers about how you run day-to-day. Operational language only." />
      <Field label="Onboarding process summary">
        <textarea value={o.onboarding_process} onChange={(e) => set("onboarding_process")(e.target.value)} data-testid="vendor-apply-onboarding-input" rows={3} className={`${inputCls} resize-y`} placeholder="How a new clinic is onboarded onto your service." />
      </Field>
      <Field label="Support process summary" className="mt-4">
        <textarea value={o.support_process} onChange={(e) => set("support_process")(e.target.value)} data-testid="vendor-apply-support-input" rows={3} className={`${inputCls} resize-y`} placeholder="Hours, channels, SLAs." />
      </Field>
      <Field label="Invoicing process summary" className="mt-4">
        <textarea value={o.invoicing_process} onChange={(e) => set("invoicing_process")(e.target.value)} data-testid="vendor-apply-invoicing-input" rows={3} className={`${inputCls} resize-y`} placeholder="Net terms, billing cadence, wholesale / contracted-rate billing capability." />
      </Field>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <Field label="States serviced">
          <input value={o.states_serviced} onChange={(e) => set("states_serviced")(e.target.value)} data-testid="vendor-apply-states-input" className={inputCls} placeholder="e.g. CA, TX, FL · or US-wide" />
        </Field>
        <div className="pt-6">
          <Toggle label="Recurring fulfillment capable" checked={o.recurring_fulfillment_capable} onChange={(v) => set("recurring_fulfillment_capable")(v)} testid="vendor-apply-recurring-toggle" />
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ form }) {
  const c = form.company;
  return (
    <div data-testid="vendor-apply-step-review">
      <StepHeading title="Review & submit" sub="One operational AI summary will be generated after you submit — to assist admin review only. Submit when ready." />
      <div className="space-y-4">
        <ReviewCard title="Company">
          <Row label="Legal name" value={c.legal_name} />
          <Row label="Website" value={c.website || "—"} />
          <Row label="HQ" value={c.headquarters_location} />
          <Row label="Primary contact" value={c.primary_contact} />
          <Row label="Support email" value={c.support_email} />
          <Row label="Category" value={VENDOR_CATEGORIES.find((x) => x.id === c.vendor_category)?.label || c.vendor_category} />
        </ReviewCard>
        <ReviewCard title={`${form.services.length} service(s)`}>
          {form.services.map((s, i) => (
            <div key={s._uid || `svc-rev-${i}`} className="border-b border-slate-100 last:border-b-0 py-2.5 first:pt-0 last:pb-0">
              <div className="text-sm font-bold text-slate-900">{s.name || <em className="text-slate-400">Unnamed</em>}</div>
              {s.description ? (<div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{s.description}</div>) : null}
              {s.products && s.products.filter((p) => p.name).length > 0 ? (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {s.products.filter((p) => p.name).map((p, j) => (
                    <span key={p._uid || `${p.name}-${j}`} className="text-[10.5px] uppercase tracking-[0.12em] font-bold rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5">
                      {p.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </ReviewCard>
        <ReviewCard title="Documents">
          {DOC_FIELDS.map((d) => (
            <Row key={d.id} label={d.label} value={form.documents[d.id] ? `${form.documents[d.id].filename}` : <span className="text-slate-400">Not uploaded</span>} />
          ))}
        </ReviewCard>
        <ReviewCard title="Operations">
          <Row label="Onboarding" value={form.operational.onboarding_process || "—"} />
          <Row label="Support" value={form.operational.support_process || "—"} />
          <Row label="Invoicing" value={form.operational.invoicing_process || "—"} />
          <Row label="States serviced" value={form.operational.states_serviced || "—"} />
          <Row label="Recurring capable" value={form.operational.recurring_fulfillment_capable ? "Yes" : "No"} />
        </ReviewCard>
      </div>
    </div>
  );
}

function ReviewCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-3">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 w-32 shrink-0">{label}</span>
      <span className="text-sm text-slate-700 break-words">{value || "—"}</span>
    </div>
  );
}

function StepHeading({ title, sub, helper }) {
  return (
    <div className="mb-6">
      <h2 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900">{title}</h2>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{sub}</p>
      {helper ? (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-[11px] font-semibold text-blue-700">
          <Info className="w-3 h-3" /> {helper}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, required, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className={labelCls}>{label} {required ? <span className="text-blue-600">*</span> : null}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
      <button type="button" role="switch" aria-checked={checked} data-testid={testid} onClick={() => onChange(!checked)} className={`relative w-9 h-5 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </button>
      <span className="font-medium">{label}</span>
    </label>
  );
}

function LandingView({ onStart }) {
  const valueProps = [
    {
      title: "Operational infrastructure for clinics",
      body: "Nodera is the lightweight operational layer that connects clinics, vendors, and consumers in the modern cash-pay healthcare economy. Not a healthcare provider — a workflow infrastructure platform.",
    },
    {
      title: "Recurring clinic utilization",
      body: "Clinics on Nodera are deploying programs designed for recurring engagement — not one-off transactions. Vendors gain predictable demand from active clinic networks.",
    },
    {
      title: "Beta — 60-second intake",
      body: "We're currently accepting vendors into the beta network with a short company-info form. Our partnerships team will follow up directly for products, documents, and operational details.",
    },
  ];
  const disclaimers = [
    "Vendors remain independently responsible for compliance.",
    "Nodera approval is operational network approval only.",
    "Nodera does not provide legal or regulatory certification.",
    "Clinics remain independently responsible for clinical decision-making.",
  ];
  return (
    <div className="bg-slate-50 min-h-screen" data-testid="vendor-apply-landing">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-14">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 mb-3">
            Vendor Application
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.04] max-w-4xl">
            Apply to Join the Nodera Vendor Network
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Connect your services to clinics deploying modern cash-pay healthcare programs through
            scalable operational infrastructure.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onStart}
              data-testid="vendor-apply-start-btn"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-7 py-3.5 text-sm font-bold transition-colors shadow-sm"
            >
              Start Application <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/contact"
              data-testid="vendor-apply-schedule-call"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-900 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-50 transition-colors"
            >
              Schedule Intro Call
            </Link>
          </div>
        </div>
      </div>

      <Section className="pt-12 pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-5">
          {valueProps.map((v) => (
            <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-2">
                Why Nodera
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">{v.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-10 rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-3">
            How it works
          </div>
          <h3 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900">
            Beta stage · one short form to get on our radar.
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
            Tell us who you are and how to reach you. That&apos;s it. Our partnerships team will follow up
            for services, documents, and operational details when we start the fit conversation.
          </p>
          <ol className="mt-5 grid sm:grid-cols-3 gap-4">
            {[
              { label: "Submit company info", desc: "~60 seconds. Six fields." },
              { label: "We review + reach out", desc: "Within 3 business days." },
              { label: "Full onboarding, if it fits", desc: "We&apos;ll send a link to complete services, docs, and ops." },
            ].map((s, i) => (
              <li
                key={s.label}
                className="rounded-xl border border-slate-200 bg-slate-50/40 p-4 flex items-start gap-3"
              >
                <span className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 flex items-center justify-center shrink-0 font-bold text-sm">
                  {i + 1}
                </span>
                <div>
                  <div className="text-sm font-bold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5" dangerouslySetInnerHTML={{ __html: s.desc }} />
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onStart}
              data-testid="vendor-apply-start-btn-2"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-3 text-sm font-semibold transition-colors"
            >
              Start Application <ArrowRight className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500">Average completion time: under 2 minutes.</span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-10 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Platform language
          </div>
          <ul className="grid sm:grid-cols-2 gap-2 text-xs text-slate-600 leading-relaxed">
            {disclaimers.map((d) => (
              <li key={d} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                {d}
              </li>
            ))}
          </ul>
        </div>
      </Section>
    </div>
  );
}

function SuccessView({ submitted, editing, navigate }) {
  return (
    <div className="bg-slate-50 min-h-screen pt-32 pb-20" data-testid="vendor-apply-success">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            {editing ? "Application resubmitted." : "Application received."}
          </h1>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
            Our partnerships team is generating an operational summary for review. You'll hear from us
            within 3 business days. If we need clarification, we'll send an email link to update your answers.
          </p>
          <div className="mt-7 inline-flex items-center gap-3 rounded-2xl bg-slate-50 border border-slate-200 px-5 py-3 text-xs text-slate-600">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Application ID</span>
            <span className="font-mono text-slate-900">{submitted?.id || "—"}</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/vendors" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-blue-700">
              ← Back to vendors page
            </Link>
            <button type="button" onClick={() => navigate("/")} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors">
              Return home <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// PublicShortApplyForm — beta-stage short form used by new public
// applicants. Collects Company Info only. Services, documents, and
// operational answers are collected LATER via the admin-initiated
// clarification-token flow (same wizard, gated by edit_token).
// -------------------------------------------------------------------
function PublicShortApplyForm({ form, setForm, onSubmit, submitting }) {
  const companyValid = validateStep("company", form).ok;
  return (
    <div data-testid="vendor-apply-page" className="bg-slate-50 min-h-screen">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 mb-3">
            Vendor Application · Beta
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.04]">
            Get on the Nodera vendor radar.
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            One short form &mdash; just company info. Our partnerships team follows up within 3 business days
            to walk through services, documents, and operational fit.
          </p>
        </div>
      </div>

      <Section className="pt-8 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
            <CompanyStep form={form} setForm={setForm} />

            <div className="mt-6 pt-5 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-slate-500 leading-relaxed max-w-md">
                Beta network. We&apos;ll email your primary contact within 3 business days. Any additional
                information (services, documents, operational details) will be requested separately.
              </p>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!companyValid || submitting}
                data-testid="vendor-apply-short-submit-btn"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-3 text-sm font-bold transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting&hellip;
                  </>
                ) : (
                  <>Submit application <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <p className="text-[11px] leading-relaxed text-slate-500">
                Vendors remain independently responsible for compliance. Nodera approval is operational
                network approval only &mdash; Nodera does not provide legal or regulatory certification.
              </p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}



function computeCompletion(form) {
  let total = 0;
  let met = 0;
  const c = form.company;
  ["legal_name", "headquarters_location", "primary_contact", "support_email", "vendor_category"].forEach((k) => {
    total += 1; if (c[k]) met += 1;
  });
  total += 1;
  if (form.services.some((s) => s.name && s.description)) met += 1;
  ["w9", "coi"].forEach((k) => { total += 1; if (form.documents[k]) met += 1; });
  ["onboarding_process", "support_process", "invoicing_process", "states_serviced"].forEach((k) => {
    total += 1; if (form.operational[k]) met += 1;
  });
  return total === 0 ? 0 : Math.round((100 * met) / total);
}

function computeMissing(form) {
  const m = [];
  const c = form.company;
  const labels = {
    legal_name: "Legal business name",
    headquarters_location: "Headquarters location",
    primary_contact: "Primary contact",
    support_email: "Support email",
    vendor_category: "Vendor category",
  };
  Object.entries(labels).forEach(([k, label]) => { if (!c[k]) m.push(label); });
  if (c.support_email && !isValidEmail(c.support_email)) m.push("Valid support email");
  if (!form.services.some((s) => s.name)) m.push("At least one service");
  if (!form.documents.w9) m.push("W-9");
  if (!form.documents.coi) m.push("Certificate of insurance");
  ["onboarding_process", "support_process", "invoicing_process", "states_serviced"].forEach((k) => {
    if (!form.operational[k]) m.push({
      onboarding_process: "Onboarding process summary",
      support_process: "Support process summary",
      invoicing_process: "Invoicing process summary",
      states_serviced: "States serviced",
    }[k]);
  });
  return m;
}

function validateStep(stepId, form) {
  if (stepId === "company") {
    const c = form.company;
    if (!c.legal_name || !c.headquarters_location || !c.primary_contact || !c.support_email || !c.vendor_category)
      return { ok: false, reason: "Complete company info" };
    if (!isValidEmail(c.support_email)) return { ok: false, reason: "Valid support email" };
    return { ok: true };
  }
  if (stepId === "services") {
    if (!form.services.some((s) => s.name && s.description))
      return { ok: false, reason: "Add at least one service" };
    return { ok: true };
  }
  if (stepId === "documents") {
    if (!form.documents.w9 || !form.documents.coi) return { ok: false, reason: "Upload W-9 and COI" };
    return { ok: true };
  }
  if (stepId === "operational") {
    const o = form.operational;
    if (!o.onboarding_process || !o.support_process || !o.invoicing_process || !o.states_serviced)
      return { ok: false, reason: "Complete operations" };
    return { ok: true };
  }
  return { ok: true };
}
